<template>
  <div class="customerReviews-section" dir="rtl">
    <div class="header container text-center my-4">
      <div class="d-flex align-items-center justify-content-center">
        <div class="flex-grow-1 border-top"></div>
        <h4 class="mx-3 font-weight-bold">آراء الخريجين والآباء</h4>
        <div class="flex-grow-1 border-top"></div>
      </div>
    </div>

    <div v-if="pageIsLoading" class="text-center py-5">
      <div class="spinner-border text-brand" role="status">
        <span class="visually-hidden">جاري التحميل...</span>
      </div>
    </div>

    <div v-else-if="reviews.length > 0" class="reviews-slider container" @mouseenter="stopAutoplay"
      @mouseleave="startAutoplay">
      <div class="reviews-viewport">
        <div class="reviews-track" :style="{ transform: `translateX(${slide * 100}%)` }">
          <div v-for="(chunk, index) in reviewChunks" :key="index" class="reviews-page"
            :aria-hidden="index !== slide ? 'true' : 'false'">
            <div v-for="review in chunk" :key="review.id" class="column-box"
              :style="{ flexBasis: `calc(${100 / chunkSize}% - 20px)` }">
              <div class="quote-mark">”</div>
              <p class="review-text" :title="review.description">{{ review.description }}</p>
              <div class="rating" :aria-label="`التقييم ${review.rate} من 5`">
                <span v-for="star in 5" :key="star" class="star" :class="{ filled: star <= review.rate }">★</span>
              </div>
              <h3 class="review-name">{{ review.name }}</h3>
            </div>
          </div>
        </div>
      </div>

      <template v-if="pageCount > 1">
        <button type="button" class="slider-arrow arrow-prev" aria-label="السابق" @click="go(slide - 1)">‹</button>
        <button type="button" class="slider-arrow arrow-next" aria-label="التالي" @click="go(slide + 1)">›</button>
        <div class="slider-dots">
          <button v-for="(chunk, index) in reviewChunks" :key="index" type="button" class="dot"
            :class="{ active: index === slide }" :aria-label="`الانتقال إلى الشريحة ${index + 1}`"
            @click="go(index)"></button>
        </div>
      </template>
    </div>

    <div v-else class="text-center py-4 text-muted">
      <p>لا توجد آراء متاحة حالياً</p>
    </div>
  </div>
</template>

<script>
import { sendRequest } from '../../utils/RequestHelper';

export default {
  name: 'CustomerReviews',
  data() {
    return {
      slide: 0,
      reviews: [],
      pageIsLoading: true,
      timer: null,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200
    }
  },
  computed: {
    chunkSize() {
      if (this.windowWidth <= 768) {
        return 1;
      } else if (this.windowWidth <= 1024) {
        return 2;
      }
      return 3;
    },
    reviewChunks() {
      const chunks = [];
      for (let i = 0; i < this.reviews.length; i += this.chunkSize) {
        chunks.push(this.reviews.slice(i, i + this.chunkSize));
      }
      return chunks;
    },
    pageCount() {
      return this.reviewChunks.length;
    }
  },
  watch: {
    pageCount(count) {
      if (this.slide >= count) this.slide = Math.max(0, count - 1);
    }
  },
  methods: {
    updateWidth() {
      this.windowWidth = window.innerWidth;
    },
    go(index) {
      this.slide = (index + this.pageCount) % this.pageCount;
      this.startAutoplay();
    },
    startAutoplay() {
      this.stopAutoplay();
      if (this.pageCount > 1) {
        this.timer = setInterval(() => {
          this.slide = (this.slide + 1) % this.pageCount;
        }, 6000);
      }
    },
    stopAutoplay() {
      clearInterval(this.timer);
      this.timer = null;
    },
    loadData() {
      this.pageIsLoading = true;
      var successCallback = (data) => {
        this.reviews = Array.isArray(data) ? data : [];
        this.pageIsLoading = false;
        this.startAutoplay();
      };
      var errorCallback = () => {
        this.reviews = [];
        this.pageIsLoading = false;
      };
      sendRequest('get', 'configuration/reviews/', false, {}, successCallback, errorCallback);
    }
  },
  mounted() {
    this.updateWidth();
    window.addEventListener('resize', this.updateWidth);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWidth);
    this.stopAutoplay();
  },
  created() {
    this.loadData();
  }
}
</script>

<style scoped>
.customerReviews-section {
  padding: 40px 20px;
  color: #333;
  margin-bottom: 2rem;
}

.header .border-top {
  border-top: 3px solid var(--brand-teal) !important;
  height: 3px;
}

.header h4 {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
}

.reviews-slider {
  position: relative;
  padding: 0 56px;
}

.reviews-viewport {
  overflow: hidden;
  padding: 12px 0 16px;
}

.reviews-track {
  display: flex;
  transition: transform 0.6s ease;
}

.reviews-page {
  flex: 0 0 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 30px;
  padding: 0 10px;
}

.column-box {
  flex-grow: 0;
  flex-shrink: 0;
  position: relative;
  background: #ffffff;
  padding: 28px 24px 22px;
  border-radius: 20px;
  border: 1px solid rgba(20, 99, 86, 0.18);
  border-top: 4px solid var(--brand-teal);
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 6px 18px rgba(20, 99, 86, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.column-box:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px rgba(20, 99, 86, 0.15);
}

.quote-mark {
  font-size: 3.5rem;
  line-height: 0.6;
  height: 28px;
  color: var(--brand-teal-light);
  opacity: 0.5;
  font-family: Georgia, serif;
}

.review-text {
  flex-grow: 1;
  font-size: 1rem;
  line-height: 1.7;
  color: #555;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 8px 0 12px;
  unicode-bidi: plaintext;
}

.rating {
  margin-bottom: 6px;
  direction: ltr;
}

.star {
  font-size: 20px;
  color: #e0e0e0;
  margin: 0 1px;
}

.filled {
  color: #ffc107;
}

.review-name {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  color: var(--brand-teal-dark);
}

.slider-arrow {
  position: absolute;
  top: calc(50% - 20px);
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: var(--brand-teal);
  color: #fff;
  font-size: 1.8rem;
  line-height: 1;
  padding-bottom: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.slider-arrow:hover {
  background: var(--brand-teal-dark);
  transform: translateY(-50%) scale(1.1);
}

/* RTL: "previous" sits on the right, "next" on the left */
.arrow-prev {
  right: 0;
}

.arrow-next {
  left: 0;
}

.slider-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
}

.dot {
  width: 10px;
  height: 10px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: rgba(20, 99, 86, 0.3);
  transition: width 0.3s ease, background-color 0.3s ease;
}

.dot.active {
  width: 24px;
  background: var(--brand-teal);
}

@media (max-width: 768px) {
  .customerReviews-section {
    padding: 20px 10px;
  }

  .header h4 {
    font-size: 1.5rem;
  }

  .reviews-slider {
    padding: 0 36px;
  }

  .column-box {
    padding: 22px 18px 18px;
    min-height: 180px;
  }

  .slider-arrow {
    width: 32px;
    height: 32px;
    font-size: 1.4rem;
  }
}
</style>
